import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Search, Droplets, User, MapPin, Calendar,
  Loader2, AlertCircle, Info, Lock, CheckCircle2, Wallet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUYER_SKU = "PDAMKAB_TEBO";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

export default function PDAMDigiflazz() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [noSambungan, setNoSambungan] = useState(searchParams.get("id") || "");
  const [isLoading, setIsLoading] = useState(false);
  const [bill, setBill] = useState(null);
  const [error, setError] = useState(null);
  const [adminFee, setAdminFee] = useState(1500);
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [checkingService, setCheckingService] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payResult, setPayResult] = useState(null);

  const loggedIn = isAgentLoggedIn();
  const customerToken = localStorage.getItem("customer_token");
  const isCustomer = !!customerToken && !isAgentLoggedIn();
  const anyLoggedIn = loggedIn || !!customerToken;
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const effectiveFee = userRole === "kasir" ? 0 : adminFee;

  useEffect(() => {
    axios.get(`${API}/admin/fees/pdam_digiflazz`)
      .then((res) => setAdminFee(res.data.fee ?? 1500))
      .catch(() => {});
  }, []);

  // Cek status layanan (toggle "Tagihan PDAM" di panel admin = key `pdam`).
  useEffect(() => {
    axios.get(`${API}/service-status`)
      .then((res) => setServiceEnabled(res.data.pdam !== false))
      .catch(() => {})
      .finally(() => setCheckingService(false));
  }, []);

  const checkBill = useCallback(async (customerNo) => {
    const id = (customerNo || "").trim();
    if (!id) {
      toast.error("Masukkan No. Sambungan");
      return;
    }
    setIsLoading(true);
    setError(null);
    setBill(null);
    try {
      const token = localStorage.getItem("user_token") || localStorage.getItem("customer_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/digiflazz/inquiry`, {
        customer_no: id,
        buyer_sku_code: BUYER_SKU,
      }, { headers });
      setBill(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal mengambil data tagihan";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCustomerPay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/customer/pdam/pay`, {
        customer_no: bill.customer_no,
        buyer_sku_code: bill.buyer_sku_code || BUYER_SKU,
      }, { headers: { Authorization: `Bearer ${customerToken}` } });
      if (res.data.balance != null) localStorage.setItem("customer_balance", res.data.balance);
      const isPending = res.data.status === "pending";
      const receipt = {
        id: res.data.transaction_id || "",
        date: new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }),
        service_type: "pdam",
        service_name: "Tagihan PDAM",
        product_name: "Tagihan PDAM",
        customer_no: bill.customer_no,
        customer_name: bill.customer_name || "",
        customer_address: bill.alamat || "",
        bill_details: billDetails,
        amount: billAmount,
        admin_fee: appFeeTotal,
        total: res.data.total || totalBayar,
        status: isPending ? "PENDING" : "BERHASIL",
        isPending,
        sn: res.data.sn || "",
        hide_cashier: true,
      };
      localStorage.setItem("last_receipt", JSON.stringify(receipt));
      toast.success("Pembayaran berhasil!");
      navigate("/receipt?return=/pdam-digiflazz");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pembayaran gagal");
      setPaying(false);
    }
  };

  useEffect(() => {
    const id = searchParams.get("id");
    if (id && anyLoggedIn) checkBill(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyLoggedIn]);

  const billAmount = bill ? (bill.selling_price || bill.price || 0) : 0;
  const details = bill?.detail && bill.detail.length > 0 ? bill.detail : [];
  const parseN = (v) => parseFloat(v || 0) || 0;

  // Build per-month rows. App admin fee (effectiveFee) is charged PER month/rekening and shown on each.
  const baseRows = details.map((d) => {
    const base = parseN(d.nilai_tagihan) + parseN(d.biaya_lain) + parseN(d.admin) + parseN(d.denda);
    return {
      bulan: d.periode,
      total_tagihan: base,
      app_admin_fee: effectiveFee,
      total: base + effectiveFee,
    };
  });

  const periodRows = baseRows.length > 0
    ? baseRows
    : (bill ? [{
        bulan: bill.periode || "Tagihan PDAM",
        total_tagihan: billAmount,
        app_admin_fee: effectiveFee,
        total: billAmount + effectiveFee,
      }] : []);

  const numMonths = periodRows.length || 1;
  const appFeeTotal = effectiveFee * numMonths;
  const totalBayar = billAmount + appFeeTotal;
  const billDetails = periodRows;

  if (checkingService) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center" data-testid="pdam-df-loading">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }

  if (!serviceEnabled) {
    return (
      <div className="min-h-screen hero-gradient" data-testid="pdam-df-maintenance">
        <header className="container mx-auto px-6 py-6">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-heading font-semibold text-slate-800">Tagihan PDAM</span>
            </div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" /> Kembali
              </Button>
            </Link>
          </nav>
        </header>
        <main className="container mx-auto px-6 py-8 max-w-md">
          <Card className="rounded-3xl border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">Sedang Perbaikan Server</h2>
              <p className="text-slate-600 mb-6">Layanan Tagihan PDAM sedang dalam perbaikan. Silakan coba lagi nanti.</p>
              <Link to="/">
                <Button className="gap-2 bg-gradient-to-r from-sky-500 to-blue-600">
                  <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="pdam-digiflazz-page">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-heading font-semibold text-slate-800">Tagihan PDAM</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="pdam-df-back-btn">
              <ArrowLeft className="w-4 h-4" /> Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 pb-16 max-w-xl">
        {!anyLoggedIn ? (
          <Card className="rounded-3xl border-0 shadow-xl">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center mx-auto">
                <Lock className="w-7 h-7 text-sky-600" />
              </div>
              <h2 className="font-heading font-bold text-lg text-slate-800">Login Diperlukan</h2>
              <p className="text-sm text-slate-500">Silakan login terlebih dahulu untuk cek tagihan PDAM. Pelanggan dapat masuk dengan Google.</p>
              <Button
                onClick={() => navigate("/login")}
                className="rounded-full h-12 px-8 bg-gradient-to-r from-sky-500 to-blue-600"
                data-testid="pdam-df-login-btn"
              >
                Login Sekarang
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Search */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="rounded-3xl border-0 shadow-xl mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4 text-xs text-sky-700 bg-sky-50 rounded-lg px-3 py-2">
                    <Info className="w-4 h-4 shrink-0" />
                    <span>Cek tagihan real-time via DigiFlazz</span>
                  </div>
                  <form
                    onSubmit={(e) => { e.preventDefault(); checkBill(noSambungan); }}
                    className="flex gap-2"
                  >
                    <Input
                      value={noSambungan}
                      onChange={(e) => setNoSambungan(e.target.value)}
                      placeholder="Masukkan No. Sambungan"
                      className="h-12 rounded-xl"
                      data-testid="pdam-df-input"
                    />
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="h-12 px-6 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600"
                      data-testid="pdam-df-check-btn"
                    >
                      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {error && (
              <Alert variant="destructive" className="mb-6" data-testid="pdam-df-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Bill result */}
            {bill && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="rounded-3xl border-0 shadow-xl overflow-hidden" data-testid="pdam-df-bill-card">
                  <div className="bg-gradient-to-r from-sky-500 to-blue-600 p-5 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Tagihan Ditemukan</span>
                    </div>
                    <p className="text-2xl font-bold" data-testid="pdam-df-customer-name">{bill.customer_name || "-"}</p>
                  </div>
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-500">No. Sambungan</span>
                        <span className="ml-auto font-mono font-semibold text-slate-800">{bill.customer_no}</span>
                      </div>
                      {bill.periode && (
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-500">Periode</span>
                          <span className="ml-auto font-semibold text-slate-800">{bill.periode}</span>
                        </div>
                      )}
                      {bill.alamat && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                          <span className="text-slate-500">Alamat</span>
                          <span className="ml-auto font-medium text-slate-700 text-right max-w-[60%]">{bill.alamat}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-dashed pt-4 space-y-2 text-sm">
                      {periodRows.length > 0 && (
                        <div className="space-y-2 mb-2" data-testid="pdam-df-breakdown">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rincian per Bulan ({periodRows.length} lembar)</p>
                          {periodRows.map((p, i) => (
                            <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`pdam-df-period-${i}`}>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{p.bulan}</p>
                                {effectiveFee > 0 && <p className="text-xs text-sky-600">Biaya Admin {formatCurrency(effectiveFee)}</p>}
                              </div>
                              <span className="font-semibold text-slate-800 text-sm">{formatCurrency(p.total)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-500">Subtotal</span>
                        <span className="font-medium text-slate-800" data-testid="pdam-df-amount">{formatCurrency(totalBayar)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold text-slate-800">Total Bayar</span>
                        <span className="font-bold text-sky-600 text-lg" data-testid="pdam-df-total">{formatCurrency(totalBayar)}</span>
                      </div>
                    </div>

                    {isCustomer ? (
                      <Button
                        onClick={handleCustomerPay}
                        disabled={paying}
                        className="w-full h-12 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 font-semibold gap-2"
                        data-testid="pdam-df-customer-pay-btn"
                      >
                        {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Wallet className="w-5 h-5" /> Bayar {formatCurrency(totalBayar)} dari Saldo</>}
                      </Button>
                    ) : (
                      <AgentPaymentButton
                        serviceType="pdam"
                        customerNo={bill.customer_no}
                        customerName={bill.customer_name || ""}
                        customerAddress={bill.alamat || ""}
                        productName="Tagihan PDAM"
                        amount={billAmount}
                        adminFee={appFeeTotal}
                        buyerSkuCode={bill.buyer_sku_code || BUYER_SKU}
                        digiflazzRefId={bill.ref_id}
                        billDetails={billDetails}
                        returnPath="/pdam-digiflazz"
                      />
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
